import React from 'react';
import {Spinner} from '@interstate/components/Spinner';
import {Button} from '@interstate/components/Button';

/**
 * Shared loading / empty / error states so every screen reads the same way. Kept intentionally
 * small and presentational — pages own the data and pass a retry handler where a failure is
 * recoverable. Each state is announced to assistive tech via role/aria-live.
 */

export function LoadingState({label = 'Loading…'}: {label?: string}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-gray-400" role="status" aria-live="polite">
      <Spinner />
      <p className="mt-4 text-sm">{label}</p>
    </div>
  );
}

/**
 * Full-screen variant for route-level gating (RequireAuth/RequireRole, the SSO redirect landing)
 * — anywhere auth state is still resolving before a page or router decision can render. Without
 * this, those spots rendered nothing, which either showed a blank flash or let a stale route
 * (e.g. Login's sign-in form) render underneath before bouncing away.
 */
export function FullScreenLoadingState({label = 'Loading…'}: {label?: string}): React.ReactElement {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <LoadingState label={label} />
    </div>
  );
}

function Panel({children}: {children: React.ReactNode}): React.ReactElement {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
      <div className="max-w-md flex flex-col items-center">{children}</div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  message,
  action
}: {
  icon?: React.ReactNode;
  title: string;
  message?: string;
  action?: {label: string; onClick: () => void};
}): React.ReactElement {
  return (
    <Panel>
      {icon && <div className="mb-4 text-gray-300">{icon}</div>}
      <h2 className="text-base font-bold text-gray-900">{title}</h2>
      {message && <p className="mt-1.5 text-sm text-gray-500">{message}</p>}
      {action && (
        <div className="mt-5">
          <Button buttonStyle="secondary" size="small" onClick={action.onClick}>
            {action.label}
          </Button>
        </div>
      )}
    </Panel>
  );
}

export function ErrorState({
  message,
  onRetry
}: {
  message: string;
  onRetry?: () => void;
}): React.ReactElement {
  return (
    <Panel>
      <div className="mb-4 text-red-300" aria-hidden>
        <WarningIcon />
      </div>
      <h2 className="text-base font-bold text-gray-900" role="alert">
        Something went wrong
      </h2>
      <p className="mt-1.5 text-sm text-gray-500">{message}</p>
      {onRetry && (
        <div className="mt-5">
          <Button buttonStyle="primary" size="small" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </Panel>
  );
}

// ── Icons (inline, heroicons-style outline) ────────────────────────────────────

export function InboxIcon(): React.ReactElement {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 0 1 2.012 1.244l.256.512a2.25 2.25 0 0 0 2.013 1.244h3.218a2.25 2.25 0 0 0 2.013-1.244l.256-.512a2.25 2.25 0 0 1 2.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 0 0-2.15-1.588H6.911a2.25 2.25 0 0 0-2.15 1.588L2.35 13.177a2.25 2.25 0 0 0-.1.661Z" />
    </svg>
  );
}

export function LockIcon(): React.ReactElement {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  );
}

function WarningIcon(): React.ReactElement {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
    </svg>
  );
}
